import { Snackbar as FreddySnackbar, IconButton, Box, Typography } from '@coolblue-development/freddy';
import { Refresh } from '@coolblue-development/icons';
import { GridSelectionModel } from '@mui/x-data-grid';
import { LicenseInfo } from '@mui/x-license';
import { useEffect, useState } from 'react';
import { AgentAllocation } from '../../../../models/AgentsAllocation';
import { Page } from '../../../../models/Page';
import { useDatagridSettings } from '../../../../utils/hooks/useDatagridSettings';
import { useSnackbar } from '../../../../utils/hooks/useSnackbar';
import { agentsDashboardColumns } from './AgentsDashboardColumns';
import { AgentsDashboardFooter } from './AgentsDashboardFooter';
import { DefaultRoutingProfileModal } from './DefaultRoutingProfileModal';
import { AgentsDashboardToolbar } from './AgentsDashboardToolbar';
import { RoutingProfileModal } from './RoutingProfileModal';
import { StyledDataGrid } from './StyledDataGrid';
import { useAgentsDashboardPage } from './useAgentsDashboardPage';

LicenseInfo.setLicenseKey(String(import.meta.env.VITE_MUI_X_LICENSE_KEY));

const AgentsDashboardPage = (): JSX.Element => {
  const [routingProfileModalOpen, setRoutingProfileModalOpen] = useState(false);
  const [defaultRoutingProfileModalOpen, setDefaultRoutingProfileModalOpen] = useState(false);
  const { fetchData, refetchAgents, agents, loading, selectedAgentIds, setSelectedAgentIds, allRoutingProfiles } =
    useAgentsDashboardPage();
  const { snackbar, show, hideSnackbar } = useSnackbar();

  // Column reordering and other grid settings no longer persist to storage since the DataGrid
  // moved off Becky onto raw @mui/x-data-grid-premium. useDatagridSettings only round-trips
  // column width; a real fix means switching it to apiRef.current.exportState()/restoreState(),
  // worth doing together with the ds-mui-x/MUI v7 migration instead of twice

  const { columns, width } = useDatagridSettings({
    columns: agentsDashboardColumns,
    storageKey: 'agents-dashboard'
  });

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const onSelectionModelChange = (selectionModel: GridSelectionModel): void => {
    setSelectedAgentIds(selectionModel);
  };

  const getSelectedAgents = (): AgentAllocation[] => {
    return agents.filter(agent => selectedAgentIds.includes(agent.id));
  };

  const handleModalSuccess = (message: string): void => {
    show(message, 'success');
    setSelectedAgentIds([]);
    void refetchAgents();
  };

  const commonModalProps = {
    selectedAgents: getSelectedAgents(),
    allRoutingProfiles,
    onError: (error: string) => show(error, 'error')
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: '0px 4px 16px 0px #246CB429',
        borderRadius: '12px',
        padding: '10px'
      }}
    >
      <Box
        sx={{
          margin: '20px 0px',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography variant="h1">{Page.AGENTS_DASHBOARD}</Typography>
        <IconButton onClick={refetchAgents} aria-label="Refresh data" title="Refresh to latest data">
          <Refresh />
        </IconButton>
      </Box>

      <StyledDataGrid
        columns={columns}
        rows={agents}
        checkboxSelection
        loading={loading}
        selectionModel={selectedAgentIds}
        onSelectionModelChange={onSelectionModelChange}
        onColumnWidthChange={width.onChange}
        localeText={{ noRowsLabel: 'Nothing to see here', filterPanelColumns: 'Column' }}
        components={{
          Toolbar: () => <AgentsDashboardToolbar />
        }}
        componentsProps={{
          baseCheckbox: { size: 'small' }
        }}
      />

      <RoutingProfileModal
        open={routingProfileModalOpen}
        onClose={() => setRoutingProfileModalOpen(false)}
        onSuccess={() => handleModalSuccess('Routing profiles updated successfully')}
        {...commonModalProps}
      />

      <DefaultRoutingProfileModal
        open={defaultRoutingProfileModalOpen}
        onClose={() => setDefaultRoutingProfileModalOpen(false)}
        onSuccess={() => handleModalSuccess('Default routing profiles set successfully')}
        {...commonModalProps}
      />

      <AgentsDashboardFooter
        selectedAgentIds={selectedAgentIds}
        onChangeChannel={() => setRoutingProfileModalOpen(true)}
        onSetDefaultRoutingProfile={() => setDefaultRoutingProfileModalOpen(true)}
        handleRefetch={refetchAgents}
      />

      <FreddySnackbar
        open={snackbar.open}
        title={snackbar.title}
        description={snackbar.description}
        intent={snackbar.intent}
        duration={snackbar.duration}
        onClose={hideSnackbar}
        placement="bottom"
      />
    </Box>
  );
};

export default AgentsDashboardPage;
